import { motion } from 'motion/react';

interface OfflineOverlayProps {
  isOffline: boolean;
}

export const OfflineOverlay = ({ isOffline }: OfflineOverlayProps) => {
  // Don't render anything if online
  if (!isOffline) {
    return null;
  }

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-brand-dark/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="bg-[#292929]/50 relative rounded-[32px] w-[320px] h-[240px] shadow-2xl border border-red-500/30"
        initial={{ scale: 0.5, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: -20 }}
        transition={{
          type: 'spring',
          damping: 20,
          stiffness: 300,
          duration: 0.6,
        }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6 p-6">
          {/* Warning Icon */}
          <motion.div
            className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center"
            animate={{
              scale: [1, 1.05, 1],
              boxShadow: [
                '0 0 20px rgba(255,85,85,0.3)',
                '0 0 30px rgba(255,85,85,0.5)',
                '0 0 20px rgba(255,85,85,0.3)',
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L1 21h22L12 2z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="white"
              />
              <path
                d="M12 9v4"
                stroke="red"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 17h.01"
                stroke="red"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>

          {/* Warning Text */}
          <div className="text-center">
            <motion.h2
              className="text-red-500 text-[22px] font-bold mb-2"
              animate={{
                textShadow: [
                  '0 0 10px rgba(255,85,85,0.5)',
                  '0 0 20px rgba(255,85,85,0.8)',
                  '0 0 10px rgba(255,85,85,0.5)',
                ],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              Connection Lost
            </motion.h2>
            <p className="text-gray-300 text-[14px] leading-relaxed">
              You're currently offline. Please check your internet connection.
            </p>
          </div>

          {/* Connection Status Indicator */}
          <motion.div
            className="flex items-center gap-2"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <span className="text-gray-400 text-[12px]">
              Attempting to reconnect...
            </span>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};
